<template>
  <component :is="isTable ? 'table' : 'div'" tabindex="0" @keydown.down.exact.prevent="trackArrowDown(1)" @keydown.up.exact.prevent="trackArrowUp(1)"
    @keydown.down.shift.exact.prevent="trackArrowDown(3)" @keydown.up.shift.exact.prevent="trackArrowUp(3)"
    @keydown.enter.prevent="selectCurrent" @keydown.space.exact="onSpaceKeydown" ref="variable_list_target"
    :class="['no-outline', { 'var-lst-grid': !isTable && columns > 1 }]"
    :style="!isTable && columns > 1 ? `--var-lst-columns: ${columns};` : ''">
    <component :is="isTable ? 'tr' : 'div'"  v-for="(el, index) in elements"
      :key="(keyPrefix ? keyPrefix : 'varlst_')+(keyId ? el['keyPrefix'] : index)"
      :style="isTable ? '' : (allowWrap ? '' : 'max-width: 600px;')"
      :ref="(el: any) => { divs[index] = el }" @mouseover="(e: any) => { index_selected = index; keyboardNav = false }"
      :class="[allowWrap ? 'var-lst-item var-lst-item-spaced' : 'q-py-none var-lst-item', { 'var-lst-item--checkbox-mode': showCheckbox }, { 'var-lst-subselection': index_selected === index && (!showCheckbox || keyboardNav) }, { 'var-lst-item-selected text-bold text-brown-10': elementSelected(index) && !showCheckbox }, { 'var-lst-item-disabled': isItemDisabled && isItemDisabled(el, index) }]"
      @mousedown="(e) => onRowMouseDown(index, e)"
      @click="(e) => onRowClick(index, e)"
      >
      <div :class="!isTable ? (allowWrap ? 'row items-start var-lst-wrap' : 'row no-wrap') : ''" :style="isTable ? 'display: contents;' : ''">
        <mosta-checkbox
          v-if="showCheckbox && !isTable"
          :class="['rec-checkbox', { 'unset-items-center': !allowWrap }]"
          @click.stop.prevent
          :model-value="elementSelected(index)"
          @update:model-value="selectElement(index)"
        />
        <div
          v-if="allowWrap"
          :class="['var-lst-slot-wrap', { 'var-lst-slot-wrap--contents': slotWrapDisplayContents }]"
        >
          <slot :item="el"></slot>
        </div>
        <slot v-else :item="el"></slot>
      </div>
    </component>
  </component>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from 'vue-facing-decorator';
import { reactive } from 'vue';
import MostaCheckbox from '@/components/common/basic/MostaCheckbox.vue';

function isInViewport(element: any, viewport: any) {
  if (!element || !viewport) { return false }
  const rect = element.getBoundingClientRect();
  const viewport_rect = viewport.getBoundingClientRect();

  return (
    rect.top >= viewport_rect.top &&
    rect.left >= viewport_rect.left &&
    rect.bottom <= viewport_rect.bottom &&
    rect.right <= viewport_rect.right
  );
}

/**
 * List component with selection functionality and hotkeys
 * */
@Component({
  name: 'VariableList',
  components: { MostaCheckbox },
  expose: ['trackArrowUp', 'trackArrowDown', 'selectCurrent', 'selectElement', 'keyboardSelectedIndex', 'selectAll', 'unselectAll', 'allSelected' ]
})
export default class VariableList extends Vue {
  // viewport to check visibility
  @Prop({ default: undefined })
  viewport!: any
  // all elements input
  @Prop({ default: [] as any[] })
  elements!: any[]
  // v-model input
  @Prop({ default: [] as any[] })
  modelValue!: any[]
  @Prop({ default: true })
  allowMultiple!: boolean
  @Prop({ default: false })
  showCheckbox!: boolean
  @Prop({ default: false })
  isTable!: boolean
  @Prop({ default: false })
  allowWrap!: boolean
  @Prop({ default: false })
  slotWrapDisplayContents!: boolean
  @Prop({ default: 1 })
  columns!: number

  @Prop({ default: 'varlst_' })
  keyPrefix?: string = 'varlst_'
  @Prop({ default: '' })
  keyId?: string = 'Id'

  /** Если задан — элемент с isItemDisabled(item, index) === true отображается серым и не выбирается */
  @Prop({ default: undefined })
  isItemDisabled?: (item: any, index: number) => boolean

  // update v-model
  @Emit("update:modelValue") emitModelValue(arg: any[]) { return arg; }
  @Emit("itemSelected") emitItemSelected(arg: any) { return arg; }
  @Emit("emptyClick") emitEmpty(arg: any) { return arg; }

  get theModel(): any[] {
    return this.modelValue
  }

  set theModel(val: any[]) {
    // emit обязателен: без него single-select не синхронизируется наверх через v-model,
    // клик по элементу в списке не закрывает меню и не «выбирает» значение в потребителе.
    this.emitModelValue(val)
    const that = this
    setTimeout(() => {
      that.emitItemSelected(undefined)
    }, 100)
  }
    
  // selection expire time
  timeout: number | undefined = undefined
  // selection index for arrow (up and down) keys
  index_selected = -1
  keyboardNav = false
  // dynamic item refs
  divs: any[] = reactive([])

  created() {
    this.index_selected = -1
  }

  /** Следующий не-disabled индекс вверх (меньший) от start, или -1 */
  nextEnabledIndexUp(start: number): number {
    if (!this.isItemDisabled || start <= 0) return start - 1
    for (let i = start - 1; i >= 0; i--) {
      if (!this.isItemDisabled(this.elements[i], i)) return i
    }
    return -1
  }

  /** Следующий не-disabled индекс вниз (больший) от start, или elements.length */
  nextEnabledIndexDown(start: number): number {
    if (!this.isItemDisabled || start >= this.elements.length - 1) return start + 1
    for (let i = start + 1; i < this.elements.length; i++) {
      if (!this.isItemDisabled(this.elements[i], i)) return i
    }
    return this.elements.length
  }

  trackArrowUp(step: number, reset_index: boolean = false) {
    this.keyboardNav = true;
    if (reset_index) {
      this.index_selected = -1;
    }

    // @ts-ignore
    const viewportEl = this.viewport?.$el || this.$refs.variable_list_target?.$el

    if (this.index_selected < 0) {
      this.index_selected = this.elements.length - 1
      for (let i = this.elements.length - 1; i >= 0; i--) {
        if (this.isItemDisabled?.(this.elements[i], i)) continue

        if (isInViewport(this.divs[i], viewportEl)) {
          this.index_selected = i
          break
        }
      }
    } else {
      let next = this.nextEnabledIndexUp(this.index_selected)
      for (let s = step - 1; s > 0 && next >= 0; s--) next = this.nextEnabledIndexUp(next)
      this.index_selected = next >= 0 ? next : this.index_selected

      if (this.index_selected >= 0 && this.divs[this.index_selected] && viewportEl &&
          // @ts-ignore
          !isInViewport(this.divs[this.index_selected], viewportEl)) {
        if (this.index_selected - 3 >= 0) {
          this.divs[this.index_selected - 3]?.scrollIntoView({ behavior: "smooth", block: "center" })
        } else {
          this.divs[0]?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }
    }

    const that = this
    // clear previous timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = undefined
    }
    // this.timeout = setTimeout(() => {
    //   that.index_selected = -1
    //   that.timeout = undefined
    // }, 2000)
  }

  trackArrowDown(step: number, reset_index: boolean = false) {
    this.keyboardNav = true;
    if (reset_index) {
      this.index_selected = -1;
    }

    // @ts-ignore
    const viewportEl = this.viewport?.$el || this.$refs.variable_list_target?.$el

    if (this.index_selected < 0) {
      this.index_selected = 0
      for (let i = 0; i < this.elements.length; i++) {
        if (this.isItemDisabled?.(this.elements[i], i)) continue
        
        if (isInViewport(this.divs[i], viewportEl)) {
          this.index_selected = i
          break
        }
      }
    } else {
      let next = this.nextEnabledIndexDown(this.index_selected)
      for (let s = step - 1; s > 0 && next < this.elements.length; s--) next = this.nextEnabledIndexDown(next)
      this.index_selected = next < this.elements.length ? next : this.index_selected

      if (this.index_selected < this.divs.length && this.divs[this.index_selected] && viewportEl &&
          // @ts-ignore
          !isInViewport(this.divs[this.index_selected], viewportEl)) {
        this.divs[this.index_selected].scrollIntoView({ behavior: "smooth", block: "center" })
      }
      if (this.index_selected >= this.elements.length && this.divs[this.divs.length - 1]) {
        this.divs[this.divs.length - 1].scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }

    const that = this
    // clear previous timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = undefined
    }
    // keep selection for 2s only
    // this.timeout = setTimeout(() => {
    //   that.index_selected = -1
    //   that.timeout = undefined
    // }, 2000)
  }

  selectCurrent(): boolean {
    return this.selectElement(this.index_selected)
  }

  /** Строка, на которую встали стрелками. Ховер тоже двигает index_selected, поэтому сам индекс
   *  не говорит о выборе с клавиатуры, и тем, кто на это опирается, нужен keyboardNav. */
  keyboardSelectedIndex(): number {
    return this.keyboardNav ? this.index_selected : -1
  }

  onSpaceKeydown(e: KeyboardEvent) {
    const target = e.target as HTMLElement | null
    if (!target) return
    if (
      target.closest('input, textarea, select, button, [contenteditable="true"], [role="textbox"], .q-field__native')
    ) {
      return
    }
    e.preventDefault()
    this.selectCurrent()
  }

  /** На отключённой строке блокируем mousedown, чтобы фокус не уходил с ячейки и не срабатывал blur в ParameterInline */
  onRowMouseDown(index: number, e: MouseEvent) {
    if (this.isItemDisabled && this.isItemDisabled(this.elements[index], index)) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  onRowClick(index: number, e: MouseEvent) {
    const rowEl = this.divs[index] as HTMLElement | undefined;
    const containsTarget = !!rowEl && rowEl.contains(e.target as Node);
    if (!rowEl || !containsTarget) {
      return;
    }
    if (this.isItemDisabled && this.isItemDisabled(this.elements[index], index)) {
      e.stopPropagation()
      return
    }
    this.selectElement(index)
  }

  // selection methods; returns true if selection was applied (emitted)
  selectElement(input_i: number): boolean {
    if (input_i < 0 || input_i >= this.elements.length) {
      return false
    }

    if (this.isItemDisabled && this.isItemDisabled(this.elements[input_i], input_i)) {
      return false
    }

    // спец. элемент «пустое значение» (добавляется в AuxSelector при allowFreeInput) — по клику эмитим emptyClick
    if (this.elements[input_i].Id === -10) {
      const that = this
      setTimeout(() => {
        that.emitEmpty(undefined)
      }, 100)
      return false
    }

    if (!this.elementSelected(input_i)) {
      if (this.allowMultiple) {
        this.theModel.push(this.elements[input_i])
        this.emitModelValue(this.theModel)

        const that = this
        setTimeout(() => {
          that.emitItemSelected(undefined)
        }, 100)

        // set starting index for keyboard
        this.index_selected = input_i
        // clear previous timeout
        if (this.timeout) {
          clearTimeout(this.timeout)
          this.timeout = undefined
        }
        return true
      } else {
        this.theModel = [this.elements[input_i]]
        return true
      }
    } else {
      this.unselectElement(input_i)
      return true
    }
  }

  // unselection methods
  unselectElement(input_i: number) {
    if (!this.theModel || input_i < 0 || input_i >= this.elements.length) { return }

    const found_el_index = this.theModel.findIndex(el => el.Id === this.elements[input_i].Id)
    if (found_el_index >= 0) {
      this.theModel.splice(found_el_index, 1)
      this.emitModelValue(this.theModel)
      const that = this
      setTimeout(() => {
        that.emitItemSelected(undefined)
      }, 100)

      // set starting index for keyboard
      this.index_selected = input_i
      // clear previous timeout
      if (this.timeout) {
        clearTimeout(this.timeout)
        this.timeout = undefined
      }
      // keep selection for 2s only
      // this.timeout = setTimeout(() => {
      //   that.index_selected = -1
      //   that.timeout = undefined
      // }, 2000)
    }
  }

  // selected getters
  elementSelected(input_i: number): boolean {
    if (!this.theModel || input_i < 0 || input_i >= this.elements.length) { return false }
    return this.theModel.find(el => el.Id === this.elements[input_i].Id) !== undefined
  }

  // mass selection
  // selection methods
  selectAll() {
    if (!this.theModel || !this.allowMultiple) { return }

    for (let inpi = 0; inpi < this.elements.length; inpi++) {
      if (this.theModel.findIndex(el => el.Id === this.elements[inpi].Id) < 0) {
        this.theModel.push(this.elements[inpi])
      }
    }
    
    this.emitModelValue(this.theModel)
    const that = this
    setTimeout(() => {
      that.emitItemSelected(undefined)
    }, 100)
  }

  // unselection methods
  unselectAll() {
    if (!this.theModel) { return }

    for (let inpi = 0; inpi < this.elements.length; inpi++) {
      const ind = this.theModel.findIndex(el => el.Id === this.elements[inpi].Id)
      if (ind >= 0) {
        this.theModel.splice(ind, 1)
      }
    }

    this.emitModelValue(this.theModel)
    const that = this
    setTimeout(() => {
      that.emitItemSelected(undefined)
    }, 100)
  }

  // selected getters
  allSelected(): boolean {
    if (!this.theModel || !this.elements.length) { return false }

    for (let inpi = 0; inpi < this.elements.length; inpi++) {
      if (this.theModel.findIndex(el => el.Id === this.elements[inpi].Id) < 0) {
        return false;
      }
    }

    return true;
  }
}
</script>

<style lang="scss">
.var-lst-item {
  cursor: pointer;
  transition: all 0.05s ease-in-out;
  border-radius: 8px;
  &:hover {
    background-color: #F2F2F2;
  }
  &.var-lst-item-selected {
    background-color: #F2F2F2;
  }
}

.var-lst-item--checkbox-mode {
  &:hover {
    background-color: transparent;
  }
}

.var-lst-subselection {
  background-color: #F2F2F2 !important;
  transition: all 0.1s ease-out;
  cursor: pointer;
}

.var-lst-item-disabled {
  opacity: 0.5;
  cursor: default;
  background-color: rgba(0, 0, 0, 0.04);
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.var-lst-item-spaced {
  padding: 4px 0;
}

.var-lst-wrap > .rec-checkbox {
  flex-shrink: 0;
  align-self: flex-start;
}

.var-lst-wrap {
  flex-wrap: nowrap;
}

.var-lst-grid {
  display: grid;
  grid-template-columns: repeat(var(--var-lst-columns), minmax(0, 1fr));
  column-gap: 12px;
}

.var-lst-slot-wrap {
  flex: 1;
  min-width: 0;
}

.var-lst-slot-wrap--contents {
  display: contents;
}

.no-outline:focus-visible {
  outline: 0;
}
</style>